<template>
  <main class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-40">
    <section class="mx-auto max-w-screen-md">
      <div class="mb-6">
        <router-link
          class="text-sm font-medium text-primary"
          to="/dashboard"
        >
          Back to Dashboard
        </router-link>
      </div>
      <div
        v-if="loading"
        class="text-sm text-gray-500"
      >
        Loading announcement...
      </div>
      <div
        v-else-if="error"
        class="text-sm text-red-600"
      >
        {{ error }}
      </div>
      <div
        v-else
        class="bg-white border border-gray-200 shadow-sm p-6"
      >
        <h1 class="text-3xl font-semibold text-gray-900">
          {{ announcement?.title }}
        </h1>
        <div class="text-sm text-gray-600 mt-2">
          {{ formatDate(announcement?.createdAt) }}
        </div>
        <p class="text-gray-700 mt-4">
          {{ announcement?.body }}
        </p>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import {defineComponent, inject, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {Announcement} from '@/types';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";

export default defineComponent({
  name: 'AnnouncementDetail',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const {settings} = inject(settingsKey)!;
    const announcement = ref<Announcement | null>(null);
    const loading = ref(false);
    const error = ref('');

    const fetchAnnouncement = async (): Promise<void> => {
      loading.value = true;
      try {
        const response = await axios.get<Announcement>(`/announcements/${props.id}`);
        announcement.value = response.data;
      } catch (err) {
        console.error('Failed to load announcement:', err);
        error.value = 'Announcement not found.';
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (value?: string): string => {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
    };

    onMounted(fetchAnnouncement);

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: announcement.value?.title || 'Announcement',
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    return {
      announcement,
      loading,
      error,
      formatDate,
    };
  },
});
</script>
